import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, CircularProgress, Grid, Typography } from "@mui/material";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom/cjs/react-router-dom.min";
import { toastAlert } from "../../helpers/toastAlert";
import { hasPermission, canAssignStaff } from "../../utils/commonutils";
import { MASTER_GROUP_LABELS } from "../../utils/masterFallbacks";
import {
  createMaster,
  deleteMaster,
  getMasters,
  normalizeMasterItems,
  updateMaster,
} from "../../services/masterService";
import { invalidateMastersCache } from "../../hooks/useMasters";
import ConfirmDeleteModal from "../CommonComponents/Confrimationmodal";

const emptyForm = { value: "", label: "", sortOrder: 0 };

const MastersPage = (props) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState(MASTER_GROUP_LABELS);
  const [selectedGroup, setSelectedGroup] = useState("inquiry_type");
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState("");
  const [deleteState, setDeleteState] = useState({ open: false, id: "" });

  const load = async () => {
    setLoading(true);
    const res = await getMasters(props, undefined, true);
    const nextItems = normalizeMasterItems(res);
    const apiGroups = res?.data?.groups;
    setItems(nextItems);
    if (Array.isArray(apiGroups) && apiGroups.length) {
      setGroups(apiGroups);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const groupItems = useMemo(
    () => items.filter((item) => item.group === selectedGroup).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)),
    [items, selectedGroup],
  );

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId("");
  };

  const handleSave = async () => {
    if (!form.value.trim() || !form.label.trim()) {
      toastAlert("Value and label are required", "error");
      return;
    }
    setSaving(true);
    const payload = {
      group: selectedGroup,
      value: form.value.trim(),
      label: form.label.trim(),
      sortOrder: Number(form.sortOrder) || 0,
    };
    const res = editingId
      ? await updateMaster(props, editingId, payload)
      : await createMaster(props, payload);
    setSaving(false);
    if (res?.status || res?.code === 200 || res?.code === 201) {
      toastAlert(editingId ? "Master item updated" : "Master item added", "success");
      invalidateMastersCache(selectedGroup);
      resetForm();
      load();
    } else {
      toastAlert(res?.message || res?.response?.data?.message || "Could not save", "error");
    }
  };

  const handleToggle = async (item) => {
    const res = item.active
      ? await deleteMaster(props, item._id)
      : await updateMaster(props, item._id, { active: true });
    if (res?.status || res?.code === 200) {
      toastAlert(item.active ? "Deactivated" : "Reactivated", "success");
      invalidateMastersCache(item.group);
      load();
    } else {
      toastAlert(res?.message || "Could not update", "error");
    }
  };

  const confirmDelete = async () => {
    const res = await deleteMaster(props, deleteState.id);
    setDeleteState({ open: false, id: "" });
    if (res?.status || res?.code === 200) {
      toastAlert("Deactivated", "success");
      invalidateMastersCache(selectedGroup);
      load();
    }
  };

  return (
    <Grid container spacing={3} className="p-2">
      <Grid item md={4} xs={12}>
        <Box className="card-main" p={2}>
          <Typography variant="h6" mb={1}>Masters</Typography>
          <Typography variant="body2" color="textSecondary" mb={2}>
            Dropdown lists used across the CRM. Add or hide values here — forms use these only.
          </Typography>
          {groups.map((group) => (
            <Box
              key={group.key}
              className={`role-card ${selectedGroup === group.key ? "active" : ""}`}
              onClick={() => {
                setSelectedGroup(group.key);
                resetForm();
              }}
            >
              <Typography fontWeight={600}>{group.label}</Typography>
              <Typography variant="caption" color="textSecondary">
                {items.filter((item) => item.group === group.key && item.active).length} active
              </Typography>
            </Box>
          ))}
        </Box>
      </Grid>
      <Grid item md={8} xs={12}>
        <Box className="card-main permission-box">
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h6">
                {groups.find((group) => group.key === selectedGroup)?.label || "Select a list"}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Stored value stays stable; label is what staff see.
              </Typography>
            </Box>
          </Box>
          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {(hasPermission("create_master") || canAssignStaff(props.authData?.role)) && (
                <Box mb={3} className="drawer-section">
                  <h3 className="drawer-section-title">{editingId ? "Edit item" : "Add item"}</h3>
                  <Box display="grid" gap={8} style={{ gridTemplateColumns: "1fr 1fr 100px auto" }}>
                    <input
                      className="input-main"
                      placeholder="Value (saved in records)"
                      value={form.value}
                      onChange={(e) => setForm({ ...form, value: e.target.value })}
                    />
                    <input
                      className="input-main"
                      placeholder="Label (shown in dropdowns)"
                      value={form.label}
                      onChange={(e) => setForm({ ...form, label: e.target.value })}
                    />
                    <input
                      className="input-main"
                      type="number"
                      placeholder="Order"
                      value={form.sortOrder}
                      onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                    />
                    <Button className="btn btn-colored" disabled={saving} onClick={handleSave}>
                      {saving ? "Saving..." : editingId ? "Update" : "Add"}
                    </Button>
                  </Box>
                  {editingId ? (
                    <Button size="small" onClick={resetForm} style={{ textTransform: "none", marginTop: 8 }}>
                      Cancel edit
                    </Button>
                  ) : null}
                </Box>
              )}
              {groupItems.length === 0 && (
                <Typography color="textSecondary">No items in this list yet.</Typography>
              )}
              {groupItems.map((item) => (
                <Box
                  key={item._id}
                  className="role-card"
                  style={{ cursor: "default", marginBottom: 8 }}
                >
                  <Box>
                    <Typography fontWeight={600}>{item.label}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      value: {item.value}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      size="small"
                      label={item.active ? "Active" : "Hidden"}
                      color={item.active ? "success" : "default"}
                    />
                    {(hasPermission("update_master") || canAssignStaff(props.authData?.role)) && (
                      <Button
                        size="small"
                        style={{ textTransform: "none" }}
                        onClick={() => {
                          setEditingId(item._id);
                          setForm({
                            value: item.value,
                            label: item.label,
                            sortOrder: item.sortOrder || 0,
                          });
                        }}
                      >
                        Edit
                      </Button>
                    )}
                    {(hasPermission("delete_master") || canAssignStaff(props.authData?.role)) && (
                      <Button
                        size="small"
                        color={item.active ? "error" : "success"}
                        style={{ textTransform: "none" }}
                        onClick={() => handleToggle(item)}
                      >
                        {item.active ? "Hide" : "Show"}
                      </Button>
                    )}
                  </Box>
                </Box>
              ))}
            </>
          )}
        </Box>
      </Grid>
      <ConfirmDeleteModal
        open={deleteState.open}
        onClose={() => setDeleteState({ open: false, id: "" })}
        onConfirm={confirmDelete}
        message="Hide this master value? Existing records keep the stored value."
      />
    </Grid>
  );
};

export default withRouter(connect((state) => ({ authData: state.authReducer }))(MastersPage));
